import { useMemo } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import InvoiceUploader from '../invoice_uploader/InvoiceUploader';
import { Button, message, Popconfirm, Popover } from 'antd';
import { isInternalTech } from '../../utils/AuthUtils';
import * as React from 'react';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { DeleteOutlined } from '@ant-design/icons';
import { markInvoiceDraftForSupplier } from '../../thunks/invoices_thunks';
import { compareStrings } from '../../utils/DataAccessUtils';
import { isClientInvoiceView } from '../invoice_utilities/InvoiceUtilities';

const InvoiceUploadButton = ({
	currentUser,
	handleInvoicePDFUploadChange,
	invoice,
	generatingPDF,
	generateInvoicePDF,
	userType,
	markInvoiceDraft,
	isDisplayOnly = false,
}) => {
	const isBuyerInvoice = useMemo(() => invoice.isBuyerUploaded || false, [invoice]);
	const isBuyerOrInternalTech = useMemo(
		() => userType === ROLE_TYPES.BUYER || isInternalTech(currentUser),
		[userType, currentUser]
	);
	const hasPDF = useMemo(
		() => invoice.invoicePDFLink && invoice.invoicePDFLink.length > 0,
		[invoice]
	);
	const pdfElement = useMemo(
		() =>
			hasPDF ? (
				<p className="mb-0">
					<a href={invoice.invoicePDFLink} target="_blank" rel="noreferrer">
						Invoice PDF
					</a>
				</p>
			) : null,
		[hasPDF, invoice.invoicePDFLink]
	);

	const deletePdf = useMemo(
		() =>
			!isBuyerOrInternalTech &&
			hasPDF &&
			[INVOICE_STATUSES.draft, INVOICE_STATUSES.pending, INVOICE_STATUSES.disputed].includes(
				invoice.status
			) ? (
				<Popconfirm
					onConfirm={() => {
						message.success('Updating Invoice');
						if (compareStrings(invoice.status, INVOICE_STATUSES.draft)) {
							handleInvoicePDFUploadChange(invoice)(null);
						} else {
							markInvoiceDraft(invoice, isClientInvoiceView(invoice, currentUser)).then((res) => {
								handleInvoicePDFUploadChange(res)(null);
							});
						}
					}}
					placement="topLeft"
					title="Are you sure you want to delete? This will move the invoice back to draft status."
					okText="Yes"
					cancelText="No"
				>
					<Button
						title="Delete"
						type="default"
						className="ml-2"
						icon={<DeleteOutlined translate="" />}
					/>
				</Popconfirm>
			) : null,
		[
			handleInvoicePDFUploadChange,
			invoice,
			hasPDF,
			isBuyerOrInternalTech,
			markInvoiceDraft,
			currentUser,
		]
	);

	const uploadButton = useMemo(() => {
		switch (invoice.status) {
			case INVOICE_STATUSES.draft:
				//Anyone can upload
				return (
					<InvoiceUploader
						roleType={userType}
						invoiceId={invoice.id}
						defaultFileList={[]}
						multiple={false}
						handleUploadSuccess={handleInvoicePDFUploadChange(invoice)}
						invoice={invoice}
					/>
				);
			case INVOICE_STATUSES.pending:
				if (isBuyerInvoice && isBuyerOrInternalTech) {
					return (
						<InvoiceUploader
							roleType={userType}
							invoiceId={invoice.id}
							defaultFileList={[]}
							multiple={false}
							handleUploadSuccess={handleInvoicePDFUploadChange(invoice)}
							invoice={invoice}
						/>
					);
				} else {
					return (
						<Popover
							content={
								isBuyerOrInternalTech
									? 'Invoices must be disputed status to allow upload of a pdf.'
									: 'Invoice must be moved back to draft status to allow upload of a pdf.'
							}
							placement={'left'}
						>
							<div>
								<InvoiceUploader
									disabled={true}
									roleType={userType}
									invoiceId={invoice.id}
									defaultFileList={[]}
									multiple={false}
									handleUploadSuccess={handleInvoicePDFUploadChange(invoice)}
									invoice={invoice}
								/>
							</div>
						</Popover>
					);
				}
			case INVOICE_STATUSES.disputed:
				//Anyone can upload
				return (
					<InvoiceUploader
						roleType={userType}
						invoiceId={invoice.id}
						defaultFileList={[]}
						multiple={false}
						handleUploadSuccess={handleInvoicePDFUploadChange(invoice)}
						invoice={invoice}
					/>
				);
		}
	}, [userType, invoice, handleInvoicePDFUploadChange, isBuyerInvoice, isBuyerOrInternalTech]);

	const uploadInvoice = [INVOICE_STATUSES.draft, INVOICE_STATUSES.disputed].some(
		(status) => status === invoice.status
	) ? (
		<InvoiceUploader
			roleType={userType}
			invoiceId={invoice.id}
			defaultFileList={[]}
			multiple={false}
			handleUploadSuccess={handleInvoicePDFUploadChange(invoice)}
			invoice={invoice}
		/>
	) : invoice.status === INVOICE_STATUSES.pending ? (
		<Popover
			content={'Invoice must be moved back to draft status to allow upload of a pdf.'}
			placement={'left'}
		>
			<div>
				<InvoiceUploader
					disabled={true}
					roleType={userType}
					invoiceId={invoice.id}
					defaultFileList={[]}
					multiple={false}
					handleUploadSuccess={handleInvoicePDFUploadChange(invoice)}
					invoice={invoice}
				/>
			</div>
		</Popover>
	) : null;
	const generateInvoice = !isBuyerOrInternalTech ? (
		<p style={{ marginBottom: 0, marginTop: 0 }}>
			Don't have an invoice file?{' '}
			<Button
				loading={generatingPDF}
				type="link"
				onClick={() => generateInvoicePDF(invoice.id)}
				style={{ marginBottom: 0, marginTop: 0 }}
			>
				Generate PDF
			</Button>
		</p>
	) : null;

	return pdfElement || uploadInvoice ? (
		<div>
			<div className="mb-1 flex flex-row items-end">
				{pdfElement} {!isDisplayOnly && deletePdf}
			</div>
			{!isDisplayOnly && uploadButton}
			{!isDisplayOnly && generateInvoice}
		</div>
	) : null;
};
const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
});
const mapDispatchToProps = (dispatch) => ({
	markInvoiceDraft: ({ id, contractorWorkOrderId }, isClientView) =>
		dispatch(markInvoiceDraftForSupplier({ id, contractorWorkOrderId }, isClientView)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(InvoiceUploadButton)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
